import { useEffect, useState } from "react";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { CrownMark } from "./Logo";

const EASE = [0.85, 0, 0.15, 1];

const HERO_BACKGROUNDS = [
  "https://images.unsplash.com/photo-1476703993599-0035a21b17a9?q=80&w=1600&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1519741497674-611481863552?q=80&w=1600&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1464349153735-7db50ed83c84?q=80&w=1600&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1519167758481-83f550bb49b3?q=80&w=1600&auto=format&fit=crop",
];

const BackgroundCarousel = () => {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(
      () => setIndex((i) => (i + 1) % HERO_BACKGROUNDS.length),
      9500
    );
    return () => clearInterval(interval);
  }, []);

  return (
    <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
      <AnimatePresence>
        <motion.img
          key={index}
          src={HERO_BACKGROUNDS[index]}
          alt=""
          initial={{ opacity: 0, scale: 1.06 }}
          animate={{ opacity: 0.35, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 2.5, ease: "easeInOut" }}
          className="absolute inset-0 h-full w-full object-cover"
        />
      </AnimatePresence>
      <div className="absolute inset-0 bg-gradient-to-b from-ink via-ink/40 to-ink" />
    </div>
  );
};

const RevealLine = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block will-change-transform"
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export const KineticHero = () => {
  const { scrollY } = useScroll();
  const glowY = useTransform(scrollY, [0, 800], [0, 220]);
  const contentY = useTransform(scrollY, [0, 800], [0, -120]);
  const contentOpacity = useTransform(scrollY, [0, 600], [1, 0]);

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden px-5 pt-24"
    >
      <BackgroundCarousel />
      <motion.div
        aria-hidden="true"
        style={{ y: glowY }}
        className="pointer-events-none absolute left-1/2 top-1/2 h-[70vmin] w-[90vmin] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(closest-side,rgba(212,175,55,0.16),rgba(217,22,4,0.06),transparent)] blur-3xl"
      />
      <motion.div
        aria-hidden="true"
        initial={{ scale: 0, rotate: -20, opacity: 0 }}
        animate={{ scale: 1, rotate: 0, opacity: 1 }}
        transition={{ duration: 1.4, delay: 0.1, ease: EASE }}
        className="pointer-events-none absolute right-[6%] top-[18%] hidden opacity-20 lg:block"
      >
        <CrownMark className="h-40 w-52" />
      </motion.div>

      <motion.div style={{ y: contentY, opacity: contentOpacity }} className="relative z-10 text-center">
        <RevealLine delay={0.35}>
          <span
            data-testid="hero-overline"
            className="mb-8 inline-flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.5em] text-gold"
          >
            <span className="h-px w-10 bg-gold/60" />
            oreida25.com.br
            <span className="h-px w-10 bg-gold/60" />
          </span>
        </RevealLine>

        <h1 className="font-serif leading-[0.9] tracking-tight">
          <RevealLine delay={0.5}>
            <span className="block text-[17vw] font-semibold text-white sm:text-[13vw] lg:text-[10rem]">
              O REI
            </span>
          </RevealLine>
          <RevealLine delay={0.65}>
            <span className="block text-[17vw] font-semibold italic sm:text-[13vw] lg:text-[10rem]">
              <span className="gold-text">da <span className="text-[1.15em]">25</span></span>
              <span className="text-royal">.</span>
            </span>
          </RevealLine>
        </h1>

        <RevealLine delay={0.95} className="mt-10">
          <p
            data-testid="hero-subtitle"
            className="mx-auto max-w-xl text-base leading-relaxed text-neutral-300 sm:text-lg"
          >
            Em breve estaremos em funcionamento. A majestade da 25 de Março,
            agora coroada no digital — luxo acessível para comprar e revender.
          </p>
        </RevealLine>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.3, ease: EASE }}
          className="mt-12 flex items-center justify-center gap-4"
        >
          <span
            data-testid="hero-coming-soon-badge"
            className="inline-flex items-center gap-3 rounded-full border border-royal/50 bg-royal/10 px-6 py-3 text-xs font-bold uppercase tracking-[0.3em] text-white"
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-royal opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-royal" />
            </span>
            Em breve
          </span>
        </motion.div>
      </motion.div>

      <motion.div
        aria-hidden="true"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-10 left-1/2 z-10 -translate-x-1/2"
      >
        <div className="flex flex-col items-center gap-3">
          <span className="text-[10px] uppercase tracking-[0.4em] text-neutral-500">Role</span>
          <motion.span
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
            className="h-10 w-px bg-gradient-to-b from-gold to-transparent"
          />
        </div>
      </motion.div>
    </section>
  );
};
